import React, { useState, useEffect, useContext, useCallback } from "react";
import moment from "moment";
import bsCustomFileInput from "bs-custom-file-input";

import { useAPIUpload, usePost } from "../API";
import { Attachment } from "../Questions/Attachment";

import ListGroup from "react-bootstrap/ListGroup";
import InterfaceContext, { ConfigurationContext } from "../Context";
import { ApplicantContext } from '../Contexts/ApplicantContext';
import { useLocalizedStrings } from "../Localization";
import TranslateWrapper from "../Translator";
import { blockify, renderLinks, safeParse, snakeToEnglish } from "../Util";
import { Note as ApplicantNote } from "@aidkitorg/aidkit/lib/program/note";
import { Badge, Button, Card, CardHeader, CardBody, CardFooter, FileInput, Icon, Modal, TextArea, useToast, Alert, Tooltip } from "@aidkitorg/component-library";
import { BadgeVariant } from "@aidkitorg/component-library/dist/types/src/Badge";

import * as Sentry from "@sentry/react";
import { LoggedInConfig } from "@aidkitorg/types/lib/config";

type NotesProps = {
  uid: string;
  canDeleteNotes: boolean;
  internalAudit?: boolean;
  unsubmittedApp?: boolean;
  relatedSupportCases?: { support_case: string; contact: string; channel: string }[];
  clickPaymentsBanner?: () => void;
  noteTags?: LoggedInConfig['capabilities']['noteTags'];
};

type Note = ApplicantNote & { kind: 'Support Case Note', relatedSupportCase: string, channel: string } | ApplicantNote;

function Notes(props: NotesProps) {
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const config = useContext(ConfigurationContext);
  const { info } = useContext(ApplicantContext);

  // Helper function to format dates for Note Tags
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return undefined;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to resolve tag value from info
  const resolveTagValue = (fieldName?: string): string | undefined => {
    if (!fieldName) return undefined;
    const fieldValue = info[fieldName as keyof typeof info];
    if (!fieldValue) return undefined;

    // Check if this is a date field that should be formatted
    const isDateField = fieldName.endsWith('_date') || fieldName.endsWith('_created_at') || fieldName.endsWith('_updated_at');

    if (typeof fieldValue === 'string') {
      if (isDateField) {
        return formatDate(fieldValue);
      }
      return snakeToEnglish(fieldValue);
    }
    return fieldValue as string | undefined;
  }

  // Helper to resolve a single tag
  const resolveTag = (fieldName: string) => {
    const value = resolveTagValue(fieldName);
    if (!value) return null;

    return {
      label: value,
      targetField: fieldName
    };
  };

  const [selectedFiles, setSelectedFiles] = useState([] as File[]);
  const addNote = usePost('/applicant/add_note');
  const addNoteWithAttachment = usePost('/applicant/add_note_with_attachment');
  const uploadURL = usePost('/document/upload_url');
  const upload = useAPIUpload('');
  const getNotes = usePost('/applicant/get_notes');
  const addContactNote = usePost('/support/create_note');
  const getContactNotes = usePost('/support/get_notes');

  const [isDeleting, setIsDeleting] = useState(false);

  const [message, setMessage] = useState("");

  const [notes, setNotes] = useState<{ data: Note[] } | undefined>();
  const refreshNotes = async () => {
    let getNotesResponse;
    let notes: Note[] = [];
    if (props.unsubmittedApp) {
      getNotesResponse = await getContactNotes({
        contact: props.uid
      });
      if (!('error' in getNotesResponse)) {
        notes = getNotesResponse.data;
      }
    } else {
      getNotesResponse = await getNotes({ applicant: props.uid, isInternalAudit: props.internalAudit });
      if (!('error' in getNotesResponse)) {
        notes = getNotesResponse.data;
      }

      if (props.relatedSupportCases && props.relatedSupportCases.length > 0) {
        let supportCaseNotes: Note[] = [];
        for (const supportCase of props.relatedSupportCases) {
          if (supportCase.contact) {
            const supportCaseNotesResponse = await getContactNotes({
              contact: supportCase.contact
            });
            supportCaseNotes = [...supportCaseNotes, ...(supportCaseNotesResponse.data || []).map((note: Note) => ({
              ...note,
              kind: 'Support Case Note',
              relatedSupportCase: supportCase.support_case,
              channel: supportCase.channel || 'default'
            }))];
          }
        }

        notes = [...notes, ...supportCaseNotes].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }
    }

    setNotes({ data: notes });
  }

  const context = useContext(InterfaceContext);

  async function saveNote() {
    if (message.length === 0) {
      alert(L.applicant.notes.please_enter_a_message);
      return;
    }
    actuallySaveNote();
  }

  async function actuallySaveNote() {
    // Build tags metadata from current appInfoTags
    const metadata: { tags?: Array<{ label: string; targetField: string }> } = {};

    if (props.noteTags?.appInfoTags) {
      const tags = props.noteTags.appInfoTags
        .map(resolveTag)
        .filter((tag): tag is { label: string; targetField: string } => tag !== null);

      if (tags.length > 0) {
        metadata.tags = tags;
      }
    }

    if (props.unsubmittedApp) {
      await addContactNote({
        contact: props.uid,
        note: message,
        ...(metadata.tags && { metadata })
      });
    } else if (selectedFiles.length > 0) {
      let savedPaths = [];
      for (const file of selectedFiles) {
        const uploadUrlResponse = await uploadURL({
          path: file.name,
          length: file.size,
        });
        if (!('error' in uploadUrlResponse)) {
          // Actually upload file
          await upload([file], {}, uploadUrlResponse.uploadURL, "PUT");
          savedPaths.push(uploadUrlResponse.savedPath);
        }
      }
      await addNoteWithAttachment({
        applicant: props.uid,
        message: message,
        savedPaths: savedPaths,
        isInternalAudit: props.internalAudit,
        ...(metadata.tags && { metadata })
      });
    } else {
      await addNote({
        applicant: props.uid,
        message,
        ...(props.internalAudit && { isInternalAudit: true }),
        ...(metadata.tags && { metadata })
      });
    }
    setMessage("");
    setSelectedFiles([]);
    await refreshNotes();
  }

  const resolveRequest = usePost('/applicant/resolve_note');
  const resolvePaymentFailure = usePost('/payments/resolve_payment_failure');

  async function doResolveRequest(note_id: string, metadata?: Record<string, any>) {
    if (metadata?.paymentTargetField) {
      await resolvePaymentFailure({
        applicant: props.uid,
        paymentTargetField: metadata.paymentTargetField,
        noteId: note_id
      });
    } else {
      await resolveRequest({ id: note_id });
    }
    await refreshNotes();
  }

  const removeSelectedFile = (f: File) => {
    const files = [...selectedFiles].filter((file) => file.name !== f.name)
    setSelectedFiles(files)
  }

  useEffect(() => {
    (async () => {
      await refreshNotes();
    })();
  }, [props.uid, props.internalAudit, props.relatedSupportCases]);

  /* For attachments with uploads */

  useEffect(() => {
    bsCustomFileInput.init();
  });

  /* For deleting notes */

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note['id']>()

  const delete_note = usePost("/applicant/delete_note");
  const delete_contact_note = usePost("/support/delete_note");

  const onCloseDeleteModal = () => {
    setShowDeleteConfirmation(false)
    setNoteToDelete("")
  }

  async function deleteNote() {
    if (!noteToDelete) return
    
    let response;
    setIsDeleting(true)

    if (props.unsubmittedApp) {
      response = await delete_contact_note({ id: noteToDelete })
    } else {
      response = await delete_note({ id: noteToDelete });
    }

    if (!response) {
      toast({
        description: L.applicant.notes.delete_error,
        variant: 'error',
      })
      Sentry.captureException(new Error("Error deleting note"))
    } else if (response.status === "ok") {
      onCloseDeleteModal()
      await refreshNotes()
    } 

    setIsDeleting(false)
  }

  return (
    <div>
      <Modal
        title="Delete note?"
        onClose={onCloseDeleteModal}
        open={showDeleteConfirmation}
      >
        <div>
          {L.applicant.notes.delete_note_msg}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => onCloseDeleteModal()}>{L.applicant.notes.cancel}</Button>
            <Button variant="primary" isDisabled={isDeleting} onClick={async () => await deleteNote()}>{L.applicant.notes.confirm_delete}</Button>
          </div>
        </div>
      </Modal >
      <Alert variant="warning" className="mb-4">
        {L.support.for_internal_reference}
      </Alert>
      <TextArea
        value={message}
        className="py-3"
        onChange={(e) => setMessage(e.target.value)}
        label={L.applicant.notes.add_note}
      />
      {props.noteTags?.appInfoTags &&
      <div className="flex flex-wrap gap-2 text-sm items-center">
        {props.noteTags.appInfoTags.map((tag, index) => {
          const resolvedTag = resolveTag(tag);
          if (!resolvedTag) return null;

          return (
            <Badge key={index} variant="neutral" style="pill">
              {resolvedTag.label}
            </Badge>
          );
        })}
        <Tooltip
          text={L.applicant.notes.tag_tooltip}
          side="bottom"
          align="center"
          variant="light"
          contentClassName="max-w-xs"
        >
          <Icon name="InformationCircleIcon" />
        </Tooltip>
      </div>
      }
      
      <div className="flex flex-wrap justify-between pb-2">
        <FileInput
          className="py-2"
          files={selectedFiles}
          onChange={(e) =>
            setSelectedFiles(Array.from(e.target.files || []))}
          onDeleteHandler={(f: File) => removeSelectedFile(f)}
        />
        <div className="ml-auto py-2">
          <Button variant="primary" onClick={saveNote} isDisabled={message.length === 0}>{L.applicant.notes.add_note}</Button>
        </div>
      </div>
      <ListGroup>
        {notes &&
          notes.data &&
          notes.data.map((n: Note) => {
            const metadata = safeParse(n.metadata || '{}') as Record<string, any>;
            let badgeText = undefined;
            let badgeVariant: BadgeVariant | undefined = undefined;

            // Always hide payments notes, as of September 2025. 
            // This is due to our new payments page!
            if (metadata?.paymentTargetField) return null;

            if (n.action_required || n.requested_change) {
              if (n.resolved || n.action_completed) {
                badgeText = L.applicant.notes.resolved
                badgeVariant = "success"
              } else {
                badgeText = L.applicant.notes.changes_requested
                badgeVariant = "error"
              }
            }
            return (
              <Card className="p-1 mb-3">
                <CardHeader
                  badgeText={badgeText}
                  badgeVariant={badgeVariant}
                />
                <CardBody className="pt-2">
                  {/* Display saved tags from metadata */}
                  {metadata?.tags && metadata.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {metadata.tags.map((tag: { label: string; targetField: string }, index: number) => (
                        <Badge key={index} variant="neutral" style="pill">
                          {tag.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="pt-2">
                    {React.createElement(TranslateWrapper, {
                      id: `note-${n.id}`,
                      desired_lang: context.lang,
                      translateText: n.message,
                      body: blockify(n.message || '', renderLinks),
                      metadata
                    })}
                  </div>
                  <div className="list-none">
                    {metadata && metadata['attachments']?.map((url: string) => {
                      return <div className="py-2"><Attachment key={url} url={url} /></div>;
                    })}
                  </div>
                  {'kind' in n && n.kind === 'Support Case Note' && (
                    <div className="mb-2 text-sm py-2 px-4 rounded-md bg-yellow-50">
                      <a href={`/supportcasev2/${n.channel}/${n.relatedSupportCase}`} className="flex items-center gap-2">
                        <Icon name="ArrowRightIcon" />{L.applicant.comms.from_support_case} {n.relatedSupportCase?.split('-')[0]}
                      </a>
                    </div>
                  )}
                </CardBody>
                <CardFooter className="pt-2">
                  <div className="text-sm flex flex-wrap justify-between gap-2 text-gray-600">
                    <div>{n.author}</div>
                    <div> {moment.unix(n.created_at).calendar()}</div>
                  </div>
                  {(((n.action_required && !n.action_completed) || (n.requested_change && !n.resolved)) || props.canDeleteNotes) &&
                      (
                        <div className="pt-2 justify-end flex gap-2">
                          {props.canDeleteNotes && (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setShowDeleteConfirmation(true);
                                setNoteToDelete(n.id || "")
                              }}
                            >{L.applicant.notes.delete}</Button>
                          )}
                          {((n.action_required && !n.action_completed) || (n.requested_change && !n.resolved)) && (
                            <Button
                              onClick={() => doResolveRequest(n.id!, safeParse(n.metadata || '{}') as Record<string, any>)}
                              variant="primary">
                              {L.applicant.notes.mark_resolved}
                            </Button>
                          )}
                        </div>
                      )
                  }
                </CardFooter>
              </Card>
            );
          })}
      </ListGroup>
    </div >
  );
}

export default Notes;
